import React, { useState, useEffect, useRef } from 'react';
import { FileText, CheckCircle, Sparkles } from 'lucide-react';
import AdSpace from '../ads/AdSpace';

interface Props {
  show: boolean;
  onComplete?: () => void;
}

const loadingSteps = [
  { message: 'Initializing resume builder...', duration: 2000 },
  { message: 'Loading professional templates...', duration: 2000 },
  { message: 'Optimizing for ATS compatibility...', duration: 2000 },
  { message: 'Preparing formatting engine...', duration: 2000 },
  { message: 'Almost ready...', duration: 2000 }
];

export default function LoadingScreen({ show, onComplete }: Props) {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const startTime = useRef(Date.now());

  useEffect(() => {
    let progressTimer: NodeJS.Timeout;
    let stepTimer: NodeJS.Timeout;
    let completionTimer: NodeJS.Timeout;

    if (!show) {
      setCurrentStep(0);
      setProgress(0);
      return;
    }

    const TOTAL_DURATION = 10000;
    const PROGRESS_INTERVAL = 50;
    const STEP_DURATION = TOTAL_DURATION / loadingSteps.length;

    progressTimer = setInterval(() => {
      const elapsed = Date.now() - startTime.current;
      const newProgress = Math.min((elapsed / TOTAL_DURATION) * 100, 100);
      setProgress(newProgress);
    }, PROGRESS_INTERVAL);

    stepTimer = setInterval(() => {
      setCurrentStep(prev => {
        if (prev >= loadingSteps.length - 1) {
          clearInterval(stepTimer);
          return prev;
        }
        return prev + 1;
      });
    }, STEP_DURATION);

    completionTimer = setTimeout(() => {
      clearInterval(progressTimer);
      clearInterval(stepTimer);
      setProgress(100);
      onComplete?.();
    }, TOTAL_DURATION);

    return () => {
      clearInterval(progressTimer);
      clearInterval(stepTimer);
      clearTimeout(completionTimer);
    };
  }, [show, onComplete]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-teal-50 via-white to-teal-100 flex flex-col z-50">
      <div className="flex flex-col flex-1">
        {/* Top Ad */}
        <div className="w-full p-4">
          <AdSpace location="resume_builder_top" className="h-24 mx-auto max-w-3xl rounded-lg shadow-lg" />
        </div>

        {/* Main Content with Side Ads */}
        <div className="flex flex-1 px-4">
          {/* Left Ad */}
          <div className="hidden lg:block w-64">
            <AdSpace location="resume_builder_left" className="h-full rounded-lg shadow-lg" />
          </div>

          {/* Center Content */}
          <div className="flex-1 flex items-center justify-center px-4 lg:px-8">
            <div className="w-full max-w-2xl bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg">
              {/* Loading Animation */}
              <div className="relative h-32 mb-8">
                <div className="absolute inset-0 flex items-center justify-center">
                  <FileText className="h-16 w-16 text-teal-600 animate-bounce" />
                </div>
                <div className="absolute top-4 left-4">
                  <Sparkles className="h-8 w-8 text-teal-400 animate-spin-slow" />
                </div>
                <div className="absolute bottom-4 right-4">
                  <CheckCircle className="h-8 w-8 text-teal-500 animate-pulse" />
                </div>
              </div>

              {/* Loading Content */}
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Creating Your Professional Resume
                </h2>
                <p className="text-gray-600 mb-8 h-6 transition-all">
                  {loadingSteps[currentStep].message}
                </p>
                
                {/* Progress Bar */}
                <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden mb-4">
                  <div 
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-teal-500 to-teal-600 transition-all duration-300 rounded-full"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                
                <p className="text-sm text-gray-500">
                  {Math.round(progress)}% complete
                </p>
              </div>
            </div>
          </div>

          {/* Right Ad */}
          <div className="hidden lg:block w-64">
            <AdSpace location="resume_builder_right" className="h-full rounded-lg shadow-lg" />
          </div>
        </div>

        {/* Bottom Ad */}
        <div className="w-full p-4">
          <AdSpace location="resume_builder_bottom" className="h-24 mx-auto max-w-3xl rounded-lg shadow-lg" />
        </div>
      </div>
    </div>
  );
}